import React, { Fragment, useEffect } from "react";
import { identifierPairs, identifierParenthetical } from "../../config/statutoryIdentifiers";
import SEO from "../../components/SEO/SEO";
import { LegalPageLayout, LegalList, LegalListItem } from "../../components/LegalPageLayout";

/**
 * The document body, shared by this page and the signup TermsAcceptanceModal
 * so there is ONE copy of the text a customer reads and signs.
 */
export const TermsContent: React.FC = () => (
  <>
        <p>
          These Terms of Service ("Terms") govern your use of the websites, applications,
          and services provided by <strong>Axoix Technologies Private Limited</strong> ("we", "us", "our"), an
          MSME-registered micro enterprise {identifierParenthetical()} with registered office at B-59,
          Second Floor, Southern Side, Block B, Rama Park Road, Uttam Nagar,
          New Delhi, Delhi - 110059, India,
          including axoix.in (our corporate website), axoix.com (our marketing site),
          and the AXOIX tenant dashboards at *.axoix.io (the "Services"). By using the
          Services you agree to these Terms.
        </p>

        <h2 className="details-content-title mb-15 mt-50">1. Eligibility & Accounts</h2>
        <p>
          You must be at least 18 and legally capable of entering into a contract. You are
          responsible for keeping your credentials confidential and for all activity under
          your account. Notify us immediately of any unauthorised use.
        </p>

        <h2 className="details-content-title mb-15 mt-50">2. Subscription Plans</h2>
        <p>
          AXOIX offers multiple tiers: Explorer (free), Officer, Commander, and Titan.
          Paid plans are billed per the schedule shown at checkout. Prices listed on
          pricing pages are exclusive of GST; GST is added on top per Indian tax law.
          Invoices are issued in a single series (INV-YYYY-NNNNN) under the brand name
          "AXOIX TECHNOLOGIES PRIVATE LIMITED".
        </p>

        <h2 className="details-content-title mb-15 mt-50">3. Payments & Refunds</h2>
        <LegalList>
          <LegalListItem>
            Payments are processed via third-party processors (e.g. Razorpay). Currency is
            INR unless otherwise stated.
          </LegalListItem>
          <LegalListItem>
            Subscriptions renew automatically unless cancelled before the next billing
            cycle.
          </LegalListItem>
          <LegalListItem>
            Refunds are at our discretion and generally not issued for partial months or
            for services already consumed. Contact{" "}
            <a href="mailto:hello@axoix.com">hello@axoix.com</a> for any refund
            enquiry.
          </LegalListItem>
          <LegalListItem>
            We may suspend the Services for non-payment after reasonable notice.
          </LegalListItem>
        </LegalList>

        <h2 className="details-content-title mb-15 mt-50">4. Acceptable Use</h2>
        <p>You agree not to:</p>
        <LegalList>
          <LegalListItem>Use the Services for illegal, infringing, or abusive purposes.</LegalListItem>
          <LegalListItem>Probe, scan, or attempt to breach the security of the Services.</LegalListItem>
          <LegalListItem>Upload malware, spam, or content that violates third-party rights.</LegalListItem>
          <LegalListItem>Reverse-engineer, scrape, or resell the Services without written consent.</LegalListItem>
          <LegalListItem>Use the Services to develop a competing product.</LegalListItem>
        </LegalList>

        <h2 className="details-content-title mb-15 mt-50">5. Your Content</h2>
        <p>
          You retain ownership of the content you upload to AXOIX ("Tenant Content"). You
          grant us a limited licence to host, process, and display your Tenant Content
          solely to provide the Services to you.{" "}
          <strong>We do not use Tenant Content to train AI models.</strong> By default,
          AXOIX runs on self-hosted AI on our own GPU server and your Tenant Content is
          not sent to any external AI provider.
        </p>
        <p className="mt-20">
          You are responsible for ensuring your content and its use by end-users complies
          with applicable law (including data protection laws in any jurisdiction where
          your end-users reside).
        </p>
        <p className="mt-20">
          <strong>Tenant Content storage:</strong> your uploaded files are stored in a
          dedicated private Backblaze B2 bucket provisioned for your tenant, in the{" "}
          <code>eu-central-003</code> region (Frankfurt, Germany). File delivery to
          browsers is routed through a Cloudflare Worker on <code>cdn.axoix.io</code>{" "}
          with HMAC-signed URLs and transient edge caching. See our Privacy Policy section
          7 for details.
        </p>

        <h2 id="section-5a" className="details-content-title mb-15 mt-50">
          5a. Bring Your Own Key (BYOK)
        </h2>
        <p>
          AXOIX offers an optional BYOK feature that lets you add an API key for a
          third-party AI provider (currently: for text and chat, OpenAI, Anthropic, Google
          (Gemini), Groq, Mistral, DeepSeek, xAI (Grok), Perplexity, Cohere, Together AI,
          OpenRouter, Hugging Face and Replicate; for speech-to-text, Groq, OpenAI and
          Sarvam; for voice, ElevenLabs and Sarvam; for images, Google (Gemini) and OpenAI)
          and route AI requests to that provider instead of our self-hosted model.
        </p>
        <p className="mt-20">
          <strong>
            By enabling BYOK and using a BYOK-routed feature, you acknowledge and agree
            that:
          </strong>
        </p>
        <LegalList>
          <LegalListItem>
            The prompt, attached files, and any other content you submit for that request{" "}
            <strong>will leave our servers</strong> and be transmitted to the third-party
            provider you selected.
          </LegalListItem>
          <LegalListItem>
            That third-party provider's terms of service, privacy policy, data retention,
            and AI training policies apply to how they handle your content — not ours. We
            do not control what they do with your content after it arrives on their
            servers.
          </LegalListItem>
          <LegalListItem>
            You are solely responsible for reading and accepting the third-party
            provider's terms, for confirming that sending your content to that provider is
            lawful under any regulations applicable to you (e.g. GDPR, DPDP Act,
            sector-specific rules), and for any data residency consequences.
          </LegalListItem>
          <LegalListItem>
            We encrypt your stored BYOK API key at rest and transmit it only over TLS to
            the provider's documented endpoint. You can deactivate or delete your BYOK key
            at any time from <em>Account → API Keys</em>. Deactivation stops new requests
            immediately; content already transmitted to the third-party provider is
            outside our control.
          </LegalListItem>
          <LegalListItem>
            Provider costs (e.g. token or usage charges billed by your chosen provider) are billed
            to you directly by that provider. We do not charge a markup or proxy those
            charges.
          </LegalListItem>
        </LegalList>
        <p className="mt-20">
          If you do not agree with this trade-off, do not enable BYOK. You will continue
          to receive AI features on our self-hosted model and your content will remain on
          our servers.
        </p>

        <h2 className="details-content-title mb-15 mt-50">6. Our Intellectual Property</h2>
        <p>
          The Services, including software, designs, logos, and AXOIX branding, are our
          property or licensed to us. Third-party themes and assets are used under their
          respective licences and are not resold or repackaged to tenants. No licence is
          granted beyond what is necessary to use the Services.
        </p>


        <h2 id="section-6a" className="details-content-title mb-15 mt-50">
          6a. Attribution — where "Powered by AXOIX" appears
        </h2>
        <p>
          Anything your customers reach runs on our infrastructure, so it carries a small
          "Powered by AXOIX" credit: your public storefront, your customer portal, the chat
          and call widgets, e-signature pages, invoice payment pages, quote signing, and
          driver tracking. Your own branding is yours throughout — the credit says who
          powers the technology, not whose business it is. It may not be removed, obscured,
          or altered.
        </p>
        <p className="mt-20">
          <strong>Inside your workspace there is one credit, on the login page, and
          nothing after it.</strong> Once your staff are signed in, no AXOIX branding
          appears anywhere in the dashboard. You can run your entire internal operation
          under your own name and logo from day one, on every plan including Explorer.
        </p>
        <p className="mt-20">
          <strong>Titan is exempt from all of the above.</strong> A Titan engagement is a
          long-term contract and may be deployed on your own infrastructure, so no
          "Powered by AXOIX" credit appears on any surface, public or internal.
        </p>
        <p className="mt-20">
          Accordingly, where we describe the platform as "white-label" we mean: your brand
          throughout, with an attribution to the technology that powers it on
          customer-facing pages and on your login screen. Removal of that attribution is
          available on Titan only.
        </p>

        <h2 className="details-content-title mb-15 mt-50">
          7. Contributor Licence Agreement (CLA)
        </h2>
        <p>
          If you contribute code or other materials to our repositories, a separate
          Contributor Licence Agreement applies. You confirm that contributions are your
          own work and that you grant us the rights described in the CLA you accept at
          application time.
        </p>

        <h2 className="details-content-title mb-15 mt-50">8. Third-Party Integrations</h2>
        <p>
          The Services integrate with third parties in the following categories. Your use
          of each integration is governed by that provider's own terms and privacy policy.
          We are not responsible for third-party outages, policy changes, pricing changes,
          or the content of third-party communications.
        </p>
        <LegalList>
          <LegalListItem>
            <strong>Infrastructure (always active):</strong> Backblaze B2 (object storage,{" "}
            <code>eu-central-003</code>), Cloudflare (DNS, TLS, WAF, CDN Worker), our VPS
            host (compute).
          </LegalListItem>
          <LegalListItem>
            <strong>Payments:</strong> Razorpay.
          </LegalListItem>
          <LegalListItem>
            <strong>Transactional email:</strong> Brevo (Sendinblue) for platform emails
            we send to you. Your tenant's operational mailboxes (IMAP/SMTP) run on our own
            Postfix/Dovecot server.
          </LegalListItem>
          <LegalListItem>
            <strong>Social media (on connect):</strong> Meta (Facebook, Instagram,
            Messenger, WhatsApp), LinkedIn, X (Twitter), Threads, Telegram — only when you
            explicitly connect an account for CHORUS publishing or messaging.
          </LegalListItem>
          <LegalListItem>
            <strong>Google (on connect):</strong> Google Calendar, Google Drive, Docs,
            Sheets, Google Ads, Google Analytics, YouTube, Google Business Profile and
            Google Classroom — only when you explicitly connect a Google account. See
            section 8a.
          </LegalListItem>
          <LegalListItem>
            <strong>Telephony:</strong> self-hosted FusionPBX for SIP routing; external
            DID and SMS carriers (e.g. LINK DID) for calls and SMS that leave our network.
          </LegalListItem>
          <LegalListItem>
            <strong>Developer identity:</strong> GitHub, for contributors signing in to
            apply, accept CLA, and receive repository invitations.
          </LegalListItem>
          <LegalListItem>
            <strong>Third-party AI (BYOK only):</strong> the providers listed in section 5a.
            Only invoked when you enable BYOK — see section 5a.
          </LegalListItem>
        </LegalList>
        <p className="mt-20">
          A canonical list of subprocessors and data flows is maintained in our Privacy
          Policy. If a category above is materially updated (new subprocessor category or
          data flow), we will notify you per section 14.
        </p>

        <h2 id="section-8a" className="details-content-title mb-15 mt-50">
          8a. Google Account Integrations
        </h2>
        <p>
          AXOIX can connect to your Google account so that Google Calendar, Google Drive,
          Docs, Sheets, Google Ads, Google Analytics, YouTube, Google Business Profile and
          Google Classroom work inside your tenant dashboard. AXOIX requests no Gmail
          permission of any kind, so no Google connection can read, send or label mail in
          your Gmail mailbox; the mail features inside AXOIX run on mailboxes we host on
          our own server. Each connection is
          optional and granted separately: you approve one service at a time on Google's own
          consent screen, and connecting initially grants sign-in only. The exact scopes we request for
          each feature, where the resulting tokens are stored, and how to revoke access are
          set out in <a href="/privacy#section-6">Privacy Policy section 6</a>.
        </p>
        <p className="mt-20">
          <strong>By connecting a Google account you agree that:</strong>
        </p>
        <LegalList>
          <LegalListItem>
            You are authorised to connect that account and to grant the scopes you approve.
            If the account belongs to an organisation (e.g. a Google Workspace domain), you
            confirm you have the necessary permission from that organisation.
          </LegalListItem>
          <LegalListItem>
            Actions AXOIX performs with the scopes you granted — creating calendar
            events, saving files, uploading videos, updating a business listing,
            creating or changing advertising campaigns, budgets and bids — are performed on
            your instruction and on your behalf, and you remain responsible for that content
            and for its compliance with Google's own policies.
          </LegalListItem>
          <LegalListItem>
            Your use of each Google service remains governed by your agreement with Google.
            We are not responsible for Google API outages, quota limits, scope or policy
            changes on Google's side, or for content Google removes or restricts.
          </LegalListItem>
          <LegalListItem>
            We use Google user data only to provide the feature you enabled. We do not sell
            it, do not use it for advertising, do not use it to train generalised AI or
            machine-learning models, and do not route it to any third-party AI provider —
            including under BYOK (section 5a).
          </LegalListItem>
          <LegalListItem>
            You may disconnect at any time from inside AXOIX and revoke our access at{" "}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
            >
              myaccount.google.com/permissions
            </a>
            . Revoking access will stop the dependent AXOIX features from working until you
            reconnect.
          </LegalListItem>
        </LegalList>
        <p className="mt-20">
          AXOIX's use and transfer of information received from Google APIs to any other app
          will adhere to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>

        <h2 className="details-content-title mb-15 mt-50">9. Availability & Support</h2>
        <p>
          We aim to keep the Services highly available but do not guarantee uninterrupted
          access. We may schedule maintenance windows, deploy updates, or deprecate
          features with reasonable notice. Support is provided per your plan via email at{" "}
          <a href="mailto:hello@axoix.com">hello@axoix.com</a>.
        </p>

        <h2 className="details-content-title mb-15 mt-50">10. Warranty Disclaimer</h2>
        <p>
          THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
          KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, OR NON-INFRINGEMENT, TO THE MAXIMUM EXTENT PERMITTED BY LAW.
        </p>

        <h2 className="details-content-title mb-15 mt-50">11. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR AGGREGATE LIABILITY UNDER THESE
          TERMS FOR ANY CLAIM IS LIMITED TO THE AMOUNTS YOU PAID US FOR THE RELEVANT
          SERVICE IN THE TWELVE (12) MONTHS BEFORE THE CLAIM AROSE. WE ARE NOT LIABLE FOR
          INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS,
          REVENUE, OR DATA.
        </p>

        <h2 className="details-content-title mb-15 mt-50">12. Termination</h2>
        <p>
          You may cancel at any time from your account settings or by contacting us. We
          may suspend or terminate your account for breach of these Terms, non-payment, or
          if required by law. Upon termination, sections that by nature should survive
          (IP, liability, governing law) will continue to apply.
        </p>

        <h2 className="details-content-title mb-15 mt-50">13. Governing Law & Jurisdiction</h2>
        <p>
          These Terms are governed by the laws of India. Courts at New Delhi shall have
          exclusive jurisdiction over any dispute arising out of or relating to these
          Terms or the Services.
        </p>

        <h2 className="details-content-title mb-15 mt-50">14. Changes</h2>
        <p>
          We may update these Terms from time to time. Material changes will be notified
          by email or site notice. Continued use after the effective date constitutes
          acceptance.
        </p>

        <h2 className="details-content-title mb-15 mt-50">15. Contact</h2>
        <p>
          Email <a href="mailto:hello@axoix.com">hello@axoix.com</a> or call{" "}
          <a href="tel:+918929222212">+91 89292 22212</a>.
        </p>
        <p className="mt-20">
          <strong>Registered office:</strong> Axoix Technologies Private Limited, B-59,
          Second Floor, Southern Side, Block B, Rama Park Road, Uttam Nagar, New Delhi,
          Delhi - 110059, India.<br />
          {identifierPairs().map(([label, value], i) => (
            <React.Fragment key={label}>
              {i > 0 && <> &nbsp;|&nbsp; </>}
              <strong>{label}:</strong> {value}
            </React.Fragment>
          ))}
        </p>
        <p className="mt-20">
          See also our <a href="/privacy">Privacy Policy</a>.
        </p>
      </>
);

const TermsPage: React.FC = () => {
  return (
    <Fragment>
      <SEO
        title="Terms of Service"
        description="Terms of Service governing your use of Axoix Technologies Private Limited services — axoix.in (corporate), axoix.com (marketing), and the AXOIX tenant dashboards at *.axoix.io."
        path="/terms"
      />
      <LegalPageLayout
        pageTitle="Terms of Service"
        pageSub="Terms"
        lastUpdated="October 3, 2026"
      >
        <TermsContent />
      </LegalPageLayout>
    </Fragment>
  );
};

export default TermsPage;
